import {
    isRouteErrorResponse,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLocation,
    useNavigate,
    useRouteError,
    useLoaderData
} from "@remix-run/react";
import type {LinksFunction, LoaderFunctionArgs, MetaFunction, TypedResponse} from "@remix-run/node";
import { json } from "@remix-run/node";
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useEffect, useLayoutEffect, useState} from 'react';
import type {ReactNode} from 'react';
import {AuthProvider} from "./lib/auth-context";
import {ProfileProvider} from "./lib/profile-context";
import {ToastProvider} from "./contexts/toast-context";
import {PendingChatProvider} from "./contexts/pending-chat-context";
import {ToastContainer} from "./design-system/components/Toast/ToastContainer";
import Header from "~/design-system/components/Story/header/Header";
import { authStateLoader, type AuthState } from "./lib/auth-state-loader";
import { authenticatedFetchProfile, type UserProfile } from "~/queries/profile";
import { TopLoadingBar } from "~/design-system/components/TopLoadingBar/TopLoadingBar";
import { PageTransition } from "~/design-system/components/PageTransition/PageTransition";

import { useMobileDetection } from "./hooks/useMobileDetection";
import { MobileOptimizationMessage } from "~/design-system/components/MobileOptimizationMessage/MobileOptimizationMessage";
import { PostHogTracker, initPostHog, reportException } from "./lib/posthog";
import { THEME_COOKIE, useAppearance, type ResolvedAppearance } from "~/hooks/useAppearance";
import { V15ThemeProvider } from "~/design-system/components/v1_5/theme-context";
import { ConfirmHost } from "~/design-system/components/v1_5/Confirm";

// Import global CSS (includes Tailwind)
import "./design-system/tokens/global.css";

// Links function for fonts and static CSS only
export const links: LinksFunction = () => [
  { rel: "icon", href: "/favicon.ico?v=2", type: "image/x-icon" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Roboto+Mono:wght@400&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Raleway:wght@400;500;600;700&family=Nunito:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Source+Sans+Pro:wght@400;600;700&family=Ubuntu:wght@400;500;700&family=Work+Sans:wght@400;500;600&family=DM+Sans:wght@400;500;700&family=Space+Grotesk:wght@400;500;700&family=Caveat:wght@400;700&display=swap",
  },
  { 
    rel: "stylesheet", 
    href: "/editor-styles.css" // Keep assuming this is a static path in public/
  },
  { 
    rel: "stylesheet", 
    href: "/fonts.css" // Keep assuming this is a static path in public/ - This likely causes CORS errors
  },
  // REMOVE the explicit link for global CSS
  // { rel: "stylesheet", href: globalStylesUrl }
];

/** The tab's name when a page gives none, so no tab ever shows the page's URL. A route's own meta replaces it. */
export const meta: MetaFunction = ({ error }) => [
  { title: isRouteErrorResponse(error) && error.status === 404 ? 'Page not found' : 'Deck' },
];

interface RootLoaderData extends AuthState {
  profile: UserProfile | null;
  isMarketingHost: boolean;
  /** What the SERVER painted, out of `THEME_COOKIE`. See `AppearanceScope`. */
  serverTheme: ResolvedAppearance;
}

/**
 * The resolved theme this browser last reported, or light when it has never reported one.
 *
 * Light for an unknown browser is not a guess about the reader — it is the only value the first
 * response can be built from, and it is the one the head style paints anyway. The client writes the
 * cookie on its first commit, so a browser is unknown exactly once.
 */
function themeFromCookie(request: Request): ResolvedAppearance {
  const header = request.headers.get('Cookie');
  if (!header) return 'light';
  // Deliberately not a cookie library: one name, two possible values, and the root loader runs on
  // every single request. A wrong or truncated value falls through to light like an absent one.
  const match = header.match(new RegExp(`(?:^|;\\s*)${THEME_COOKIE}=(light|dark)(?:;|$)`));
  return match ? (match[1] as ResolvedAppearance) : 'light';
}

export async function loader({ request }: LoaderFunctionArgs): Promise<TypedResponse<RootLoaderData>> {
  const { data, headers } = await authStateLoader(request);

  let profile: UserProfile | null = null;

  if (data.user && data.token) {
    try {
      profile = await authenticatedFetchProfile(data.token)();
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }

  // usedeck.ai is ONE domain. The bare public landing renders ONLY for logged-OUT visitors at "/";
  // logged-in users (and every other route) get the app chrome — the index route redirects a signed-in
  // visitor at "/" to the dashboard. So `isMarketingHost` now means "show the landing layout" =
  // (homepage AND signed-out), NOT a hostname check. (The old marketing-vs-app host split + its 404 gate
  // were removed so the app is fully reachable on usedeck.ai itself, no app. subdomain.)
  // "?landing" forces the marketing layout even when signed in, so the public landing
  // can be previewed without signing out (pairs with the same bypass in _index.tsx).
  const rootUrl = new URL(request.url);
  // "?landing-2" is the dark rebuild behind the same rule.
  // The case study landing and the library's case studies are public pages for everyone, signed in or not,
  // phone included.
  const isMarketing =
    (rootUrl.pathname === '/' &&
      (!data.user || rootUrl.searchParams.has('landing') || rootUrl.searchParams.has('landing-2'))) ||
    rootUrl.pathname === '/cases/landing' ||
    rootUrl.pathname.startsWith('/cases/library/');

  return json({
    ...data,
    profile,
    serverTheme: themeFromCookie(request),
    isMarketingHost: isMarketing,
  }, { headers });
}

/**
 * An error page as a whole document. The root has no `Layout` export, so what its boundary returns IS
 * the response: a bare `<div>` shipped with no `<head>`, and the tab showed the page's URL. `<Meta />`
 * reads the root meta, which names the 404.
 */
function ErrorDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  // Chrome DevTools asks every site for this file; its 404 is noise, not an error.
  const devtoolsProbe =
    !!error &&
    typeof error === 'object' &&
    'data' in error &&
    String(error.data).includes('/.well-known/appspecific/com.chrome.devtools.json');
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  // React caught this in the boundary, so it never reaches window.onerror: report it to PostHog
  // explicitly so boundary errors still land in Error Tracking. In an effect, so it waits for
  // hydration, and PostHog is started here because the tracker that normally starts it is not mounted.
  useEffect(() => {
    if (devtoolsProbe || notFound) return;
    initPostHog();
    reportException(error);
  }, [error, devtoolsProbe, notFound]);

  if (devtoolsProbe) return <div style={{ display: 'none' }} />;

  if (notFound) {
    return (
      <ErrorDocument>
        <div style={{
          padding: '2rem',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
          fontFamily: "'Inter Display', 'Inter', sans-serif",
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Page not found</h1>
          <p style={{ color: '#71717a' }}>The page you're looking for doesn't exist.</p>
        </div>
      </ErrorDocument>
    );
  }

  console.error("App error boundary caught:", error);

  const errorDetails =
    error instanceof Error
      ? `${error.name}: ${error.message}\n\n${error.stack ?? ''}`
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}\n\n${typeof error.data === 'string' ? error.data : JSON.stringify(error.data, null, 2)}`
        : (() => {
            try {
              return JSON.stringify(error, null, 2);
            } catch {
              return String(error);
            }
          })();

  return (
    <ErrorDocument>
      <div style={{
        padding: '2rem',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <h1>Something went wrong</h1>
        <p>We encountered an unexpected error. Please try again.</p>

        {import.meta.env.DEV && (
          <pre
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f4f4f5',
              color: '#b91c1c',
              padding: '1rem',
              borderRadius: '0.5rem',
              fontSize: '12px',
              lineHeight: 1.5,
              maxHeight: '50vh',
              overflow: 'auto',
              marginTop: '1rem',
            }}
          >
            {errorDetails}
          </pre>
        )}

        <div style={{ marginTop: '2rem' }}>
          <button type="button" 
            onClick={() => navigate('/sign-in')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              marginRight: '1rem'
            }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    </ErrorDocument>
  );
};

// Create a layout component that includes the Header
function Layout({ serverUser, isMarketingHost }: { serverUser: AuthState['user']; isMarketingHost: boolean }) {
  const location = useLocation();
  // Read the store directly rather than the context: this is the same value `AppearanceScope`
  // provides, and taking it from the source keeps `Layout` usable outside that provider.
  const { resolved } = useAppearance();
  const { isMobile, isLoading } = useMobileDetection();
  // The whole signed-out auth chain, not just the form: the magic-link callback screens own the
  // full viewport too, and the error one is a static block that used to sit under this header and
  // push the page into a scroll.
  const isSignInPage =
    location.pathname === "/sign-in" ||
    location.pathname === "/yc" ||
    location.pathname.startsWith("/auth/callback") ||
    location.pathname === "/auth/confirm" ||
    // The OAuth consent page: a person arrives from claude.ai or Cowork and goes straight back.
    location.pathname === "/connect";
  const isHomePage = location.pathname === "/home";

  // The four settings pages now mount `SettingsRouteShell`, which draws its own application bar and
  // its own rail. They have to be here or the app header stacks a second bar on top of that one.
  // `/brand-kits` is in the list for the same reason as the four above it — it renders the same
  // `SettingsShell`. It briefly was NOT, and the comment here said so, because the settings pages
  // and the kits page moved to the shell in that order: for a moment the kits list genuinely still
  // drew `AccountLayout` and genuinely still wanted the app header. Both halves of that were true
  // when written and the pair was a double bar. The kit DETAIL route needs no entry — the
  // `startsWith("/brand-kits/")` clause below already covers it.
  // `/dashboard` draws v1.5's own `Dashboard/DashboardBar` — mark, wordmark, "New deck", account —
  // over its own dark ground. The global header here would be a second bar above it, in the light
  // palette, on a page that is no longer light.
  const routesWithOwnHeader = ["/home", "/dashboard", "/project/add", "/onboarding", "/deck-gen", "/unsubscribe", "/profile", "/billing", "/integrations", "/settings", "/brand-kits", "/skills"];
  const usesOwnHeader = routesWithOwnHeader.includes(location.pathname) ||
        location.pathname.startsWith("/deck/") ||
        location.pathname.startsWith("/brand-kits/") ||
        location.pathname.startsWith("/canvas/") ||
        location.pathname.startsWith("/lab") ||
        location.pathname.startsWith("/birtvy") ||
        location.pathname.startsWith("/artifact-agent") ||
        location.pathname.startsWith("/prototype/") ||
        location.pathname.startsWith("/internal/");
  
  // Determine if this is an account or project page that should use muted background
  const accountPages = ["/profile", "/pricing", "/billing", "/settings", "/integrations", "/brand-kits", "/skills"];
  const isAccountPage = accountPages.some(page => location.pathname === page);
  // The account-management pages (everything but Pricing) expose the account menu via the profile sidebar,
  // so the header avatar is redundant there — and they use a transparent header. Pricing keeps the white card.
  const accountManagementPages = ["/profile", "/billing", "/integrations", "/settings", "/brand-kits", "/skills"];
  const isAccountManagementPage = accountManagementPages.includes(location.pathname);
  const hideHeaderAvatar = isAccountManagementPage;
  const isProjectPage = location.pathname.startsWith('/project');
  const isDashboardPage = location.pathname === '/dashboard';

  // Dashboard + account-management pages: transparent header (no bg). Pricing: card (white) header.
  const headerBackgroundVariant = isDashboardPage || isAccountManagementPage ? 'transparent' : isAccountPage ? 'card' : isProjectPage ? 'muted' : 'card';

  // Content background: account pages use default (white), project pages muted.
  //
  // Both of those are LEGACY `--color-*` tokens and that ramp is light-only — `--color-default` is
  // `--color-zinc-00` and `--color-muted` is `--color-zinc-100`, i.e. white and near-white, in every
  // theme. Painting them in Dark lays a white sheet over the dark ground, which is exactly what made
  // settings and the deck read white with Dark selected. In Dark the scroller paints nothing and the
  // document's own ground shows through; the day the legacy ramp gets a dark twin, this goes away.
  const contentBackgroundColor =
    resolved === 'dark'
      ? undefined
      : isAccountPage
        ? 'var(--color-default)'
        : isProjectPage
          ? 'var(--color-muted)'
          : undefined;

  if (isMarketingHost) {
    return (
      <PageTransition>
        <Outlet />
      </PageTransition>
    );
  }

  // The desktop shell's agent panel is a ~420px rail INSIDE the app, not a phone. It renders bare: no
  // header, and never the mobile gate, which would otherwise tell a desktop app to open on desktop.
  if (location.pathname === "/local-agent") {
    return <Outlet />;
  }

  // Show mobile optimization message for mobile users
  if (isMobile) {
    return <MobileOptimizationMessage />;
  }

  // The parent of Layout (in App component) will be the main flex container
  return (
    <>
      {!isSignInPage && !usesOwnHeader ? (
        <Header 
          serverUser={serverUser} 
          minimal={isHomePage}
          simplified={isAccountPage}
          showAvatar={!hideHeaderAvatar}
          backgroundVariant={headerBackgroundVariant}
          showBackButton={isAccountPage}
        />
      ) : null}
      {/* This div should grow to fill remaining space and handle its own overflow */}
      <div className="grow overflow-y-auto" style={{ marginTop: 0, backgroundColor: contentBackgroundColor }}>
        <PageTransition>
          <Outlet />
        </PageTransition>
      </div>
    </>
  );
}

/**
 * The appearance preference, applied to every v1.5 surface at once.
 *
 * Mounted here rather than per route because the preference is one fact about the app and the parts
 * that read it are on every page — the header, the dashboard, the deck workspace's chrome, the
 * settings rail. Each v1.5 part still takes a `theme` prop; what this supplies is the DEFAULT when
 * nobody passed one, which is the case for all 389 of them. See `v1_5/theme-context.tsx`.
 *
 * `resolved` and not `appearance`: `system` is a rule, not a theme, and the store has already folded
 * it through the device's own setting.
 *
 * The legacy design system is NOT covered by this and deliberately so — `themes.css` defines one
 * light ramp and the head style above pins `color-scheme: light`. Anything still built on
 * `--color-*` (the canvas stack, `Modal`, the planning chat, the landing page) stays light until
 * that ramp has a dark twin, which is a palette to design rather than plumbing to write.
 */
/**
 * `useLayoutEffect` on the client, `useEffect` on the server.
 *
 * Nothing clever — React warns when `useLayoutEffect` runs in a component it also renders on the
 * server, since there is no layout to measure there. The client is where the DOM write has to be
 * early, so it keeps the layout timing and the server takes the no-op.
 */
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

function AppearanceScope({ serverTheme, children }: { serverTheme: ResolvedAppearance; children: React.ReactNode }) {
  const { resolved } = useAppearance();

  /*
   * The first render is the SERVER's answer, not the store's — and on the client too.
   *
   * The store cannot answer on the server (localStorage, a media query) so it returns light there,
   * and it returns light on the client's first pass as well, because it only reads the browser once
   * `subscribe` has run in an effect. That meant EVERY surface React draws — the dashboard's ground,
   * the deck lane, the composer card, which is the biggest opaque shape on the page — was painted in
   * the light palette until hydration finished and one more commit landed. The document ground was
   * already dark by then (the head script), so what the reader got was a white card and a white
   * greeting sitting on a dark page for as long as the load took.
   *
   * `serverTheme` is the cookie the client wrote last time, so the markup arrives correct. Holding
   * it for the hydration pass as well is the whole point: React compares its first client render
   * against the HTML, and handing it a different theme there would throw the markup away and repaint
   * — the identical flash, one step later.
   */
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const theme = hydrated ? resolved : serverTheme;

  /*
   * Context reaches React. The DOCUMENT does not.
   *
   * `<html>`'s own background is painted by the head style below, which is a static string with no
   * way to learn the preference — so Dark drew dark chrome on a white page and every gap between
   * components showed it. Stamping the resolved theme on the root element is what gives that style
   * something to key off, and this is its only writer: everything else reads the context.
   *
   * `colorScheme` beside it, because the parts nobody styles are the ones that give it away —
   * scrollbars, form controls, autofill, the overscroll gutter all stay light otherwise.
   *
   * A layout effect so the attribute lands in the frame of the first client render rather than one
   * paint later; `useEffect` on the server, where there is no layout pass to run before.
   */
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.appearance = theme;
    root.style.colorScheme = theme;
    // ...and tell the next request what to render, so this whole dance is a no-op next time. A year,
    // `Lax` because nothing here is worth sending to another site's request, and no `Secure` flag so
    // it also works over plain http on localhost.
    document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax`;
  }, [theme]);

  return <V15ThemeProvider theme={theme}>{children}</V15ThemeProvider>;
}

export default function App() {
  const [queryClient] = useState(() => new QueryClient());
  const { user: serverUser, profile, isMarketingHost, serverTheme } = useLoaderData<RootLoaderData>();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider serverUser={serverUser}>
        <ProfileProvider serverProfile={profile}>
          <PendingChatProvider>
            <ToastProvider>
                {/* Ensure html and body are full height */}
                {/* `data-appearance` server-side, so the ground below is correct in the FIRST bytes
                    the browser parses — before the head script, before hydration, and even with JS
                    off. The script that follows still runs: localStorage is the source of truth and
                    this is the cookie's copy of it, so the script is what corrects a stale one. */}
                <html
                    lang="en"
                    suppressHydrationWarning={true}
                    className="h-full"
                    data-appearance={serverTheme}
                    style={{ colorScheme: serverTheme }}
                >
                <head key="head">
                    <meta charSet="utf-8"/>
                    {/* Chrome Origin Trial: HTMLInCanvas (drawElementImage) for usedeck.ai + subdomains,
                        through 2026-10-20. Real Chrome users get html-in-canvas with no browser flag;
                        benchTextures/htmlBake auto-detect it and use it over the SVG thumbnail path. */}
                    <meta httpEquiv="origin-trial" content="ArUDAsqQYwGtpXWQFT7/LakaAztkR/U0lhovpNNNAifCSnBKRdyLn4uHtrtBfe9cTdjuviC+h8BkrDQik6I3ggMAAABjeyJvcmlnaW4iOiJodHRwczovL3VzZWRlY2suYWk6NDQzIiwiZmVhdHVyZSI6IkhUTUxJbkNhbnZhcyIsImV4cGlyeSI6MTc5MjQ1NDQwMCwiaXNTdWJkb21haW4iOnRydWV9"/>
                    <meta name="viewport"
                          content="width=device-width, initial-scale=1.0"/>
                    {/* Deck's UI is light-only. Say so, or a host whose canvas defaults to dark renders our
                        dark text on its dark surface: the dashboard hero was unreadable in Claude's in-app
                        browser (and in any webview with a dark base) because NOTHING in the app painted an
                        opaque page background — html, body and the hero all computed rgba(0,0,0,0), so the
                        embedder's dark surface showed through. This also keeps UA-styled bits (form controls,
                        scrollbars, autofill) light everywhere. */}
                    <meta name="color-scheme" content="light"/>
                    <style dangerouslySetInnerHTML={{
                        __html: `
                html, body { margin: 0; padding: 0; }
                /* The page's own background, not the host's. Paired with the color-scheme meta above: the
                   meta covers browsers that honour it, this covers webviews that hard-set a dark base.
                   Routes that want their own backdrop (canvas, present mode, landing) paint over it. */
                :root { color-scheme: light; }
                html, body { background-color: #ffffff; }
                /* ...until the reader has asked for Dark, at which point that opaque white IS the bug:
                   the chrome went dark and the page under it did not. AppearanceScope stamps
                   data-appearance on the root element; this is the rule that reads it. Both selectors
                   are (0,2,x) against the (0,0,1) above, so the override does not lean on source
                   order. The colour is written out rather than taken from var(--color-surface-1)
                   because this style is inline in the head and has to hold before the token sheet has
                   loaded — it IS that token's value, hsl(240 3% 8%), and the two move together.
                   (No backticks in here: the whole block is a JS template literal.) */
                :root[data-appearance='dark'] { color-scheme: dark; }
                :root[data-appearance='dark'],
                :root[data-appearance='dark'] body { background-color: hsl(240 3% 8%); }
                /* Kill Chromium/Edge/Dia two-finger swipe-left = browser "back" navigation. The horizontal
                   overscroll gesture is a document-level action, so suppressing it on the canvas alone is
                   unreliable; disabling it on the root scroller covers every surface.
                   BOTH AXES, not just X. The app is a fixed-height shell that never scrolls its document,
                   so the vertical rubber band had nothing to reveal but the page stretching away from its
                   own edges under a trackpad — motion that says "there is more here" about a layout where
                   there is not (Beka, 2026-09-18). Pull-to-refresh goes with it, which on a workspace that
                   holds unsaved edits is a gesture worth losing. */
                html, body { overscroll-behavior: none; }
              `
                    }}/>
                    {/*
                        Blocking, tiny, and in the head on purpose.

                        The preference lives in localStorage, so the SERVER cannot know it — SSR always
                        emits the light ground, and the effect in AppearanceScope only runs once React
                        has hydrated. That gap is a full white page flashed at every reader on Dark
                        before the app catches up, which is the same defect as a page that never turned
                        dark at all, just shorter. This runs during parse, before the first paint, and
                        writes exactly what AppearanceScope would have written; the effect then sets the
                        same two values again and the DOM does not move.

                        It duplicates the store's key and its system rule rather than importing them,
                        because nothing bundled can run this early. Both are one line each and both are
                        named here so a change to useAppearance has something to grep for: 'deck:appearance'.

                        try/catch because localStorage throws outright in a webview with site data
                        blocked, and a head script that throws takes the page with it. The fallback is
                        the light ground already painted above.
                    */}
                    <script dangerouslySetInnerHTML={{
                        __html: `(function(){try{var a=localStorage.getItem('deck:appearance');var r=(a==='light'||a==='dark')?a:(window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light');var e=document.documentElement;e.dataset.appearance=r;e.style.colorScheme=r;}catch(e){}})();`
                    }}/>
                    <Meta/>
                    <Links/>
                </head>
                {/* Body is a flex column, taking at least screen height */}
                <body
                    className="h-full flex flex-col m-0 p-0 antialiased"
                    style={{
                        fontFamily: "'Inter Display', 'Inter', sans-serif",
                        minHeight: '100vh'
                    }}
                    suppressHydrationWarning={true}
                >
                <TopLoadingBar/>
                <PostHogTracker user={serverUser}/>
                <AppearanceScope serverTheme={serverTheme}>
                    <Layout serverUser={serverUser} isMarketingHost={isMarketingHost}/>
                    {/* Inside the scope, not beside it: the dialog reads the appearance preference
                        through V15ThemeProvider, and a sibling would silently fall back to dark. */}
                    <ConfirmHost/>
                </AppearanceScope>
                <ToastContainer/>
                <ScrollRestoration/>
                <Scripts/>
                </body>
                </html>
            </ToastProvider>
          </PendingChatProvider>
        </ProfileProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}